import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import DateTextPicker from '../components/DateTextPicker';
import './Customers.css';
import './BilledMatrix.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function firstOfYear() { return `${new Date().getFullYear()}-01-01`; }
function today() { return new Date().toISOString().slice(0, 10); }

/** Semantic tone for matrix cells (matches legend swatches). */
function matrixCellTone(from, to, row, ym, amount) {
  const monthStart = `${ym}-01`;
  const [y, m] = ym.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const monthEnd = `${ym}-${String(lastDay).padStart(2, '0')}`;
  const sowStart = row.sow_start || '';
  const sowEnd = row.sow_end || '';
  const inPeriod = sowStart && sowEnd && sowStart <= monthEnd && sowEnd >= monthStart;
  const beforeFrom = monthEnd < from;
  const afterTo = monthStart > to;
  const has = amount > 0;
  if (inPeriod && !has && !beforeFrom && !afterTo) return 'gap';
  if (afterTo && has) return 'warn';
  if (inPeriod && has) return 'ok';
  if (beforeFrom && has) return 'warn';
  return '';
}

export default function BilledMatrix() {
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selCustomers, setSelCustomers] = useState([]);
  const [selGroups, setSelGroups] = useState([]);
  const [selEmps, setSelEmps] = useState([]);
  const [data, setData] = useState({ months: [], rows: [], has_sub_brand: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);

  useEffect(() => {
    api.get('/customers').then(setCustomers).catch(() => {});
    api.get('/settings/customer-groups').then(setGroups).catch(() => {});
    api.get('/employees').then(setEmployees).catch(() => {});
  }, []);

  const run = () => {
    setLoading(true); setErr('');
    setPage(1);
    const qs = new URLSearchParams({ from, to });
    if (selCustomers.length) qs.append('customer_ids', selCustomers.join(','));
    if (selGroups.length) qs.append('customer_group_ids', selGroups.join(','));
    if (selEmps.length) qs.append('emp_ids', selEmps.join(','));
    api.get(`/reports/billed-matrix?${qs.toString()}`)
      .then(d => setData(d || { months: [], rows: [] }))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(data.rows.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [data.rows.length, pageSize]);

  const fmt = n => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const totalsByMonth = useMemo(() => {
    const t = {};
    data.months.forEach(m => { t[m] = 0; });
    data.rows.forEach(r => Object.entries(r.amounts || {}).forEach(([m, v]) => { t[m] = (t[m] || 0) + Number(v || 0); }));
    return t;
  }, [data]);

  const grandTotal = data.rows.reduce((s, r) => s + Object.values(r.amounts || {}).reduce((a, b) => a + Number(b || 0), 0), 0);

  const scopeFilterCount = selCustomers.length + selGroups.length + selEmps.length;

  const customerFilterOptions = useMemo(
    () => customers.map(c => ({ value: c.customer_id, label: c.legal_name || c.customer_id })),
    [customers]
  );
  const groupFilterOptions = useMemo(
    () => groups.map(g => ({ value: String(g.id), label: g.name || String(g.id) })),
    [groups]
  );
  const employeeFilterOptions = useMemo(
    () => employees.map(e => ({ value: e.emp_id, label: `${e.emp_id} · ${e.full_name || ''}` })),
    [employees]
  );

  const fixedCols = 6 + (data.has_sub_brand ? 1 : 0);
  const emptyColSpan = fixedCols + data.months.length + 1;

  const totalRows = data.rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = data.rows.slice(offset, offset + pageSize);
  const rangeStart = totalRows === 0 ? 0 : offset + 1;
  const rangeEnd = totalRows === 0 ? 0 : Math.min(offset + pageSize, totalRows);

  return (
    <>
      <div className="page-header">
        <h1>Billed Matrix</h1>
      </div>
      <div className="page-content billed-matrix-page">
        <div className="customers-top billed-matrix-stats-top">
          <div className="customers-stats" aria-label="Report summary">
            <div className="customers-stat">
              <div className="customers-stat-label">Date range</div>
              <div className="customers-stat-value tabular-nums">
                {loading ? '—' : `${from} → ${to}`}
              </div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Rows</div>
              <div className="customers-stat-value tabular-nums">{loading ? '—' : data.rows.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Total billed</div>
              <div className="customers-stat-value tabular-nums">{loading ? '—' : `₹${fmt(grandTotal)}`}</div>
            </div>
          </div>
        </div>

        <div className="card billed-matrix-filters-card">
          <details className="billed-matrix-filters-details" open>
            <summary className="billed-matrix-filters-summary">
              <span className="billed-matrix-filters-chevron" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="billed-matrix-filters-summary-title">Filters</span>
              <span className="billed-matrix-filters-summary-hint tabular-nums">
                {from} → {to}
              </span>
              {scopeFilterCount > 0 ? (
                <span className="billed-matrix-filters-scope-badge">{scopeFilterCount}</span>
              ) : null}
            </summary>
            <div className="billed-matrix-filters-details-body" id="billed-matrix-filters-panel">
              <div className="form-grid billed-matrix-filter-grid">
                <DateTextPicker id="billed-matrix-from" label="From date" value={from} onChange={setFrom} />
                <DateTextPicker id="billed-matrix-to" label="To date" value={to} onChange={setTo} />
                <div className="form-group billed-matrix-filter-dropdown">
                  <MultiSelectDropdown
                    id="billed-matrix-customers"
                    label="Customers"
                    placeholder="All customers"
                    options={customerFilterOptions}
                    value={selCustomers}
                    onChange={setSelCustomers}
                  />
                </div>
                <div className="form-group billed-matrix-filter-dropdown">
                  <MultiSelectDropdown
                    id="billed-matrix-groups"
                    label="Customer groups"
                    placeholder="All groups"
                    options={groupFilterOptions}
                    value={selGroups}
                    onChange={setSelGroups}
                  />
                </div>
                <div className="form-group billed-matrix-filter-dropdown">
                  <MultiSelectDropdown
                    id="billed-matrix-emps"
                    label="Employees"
                    placeholder="All employees"
                    options={employeeFilterOptions}
                    value={selEmps}
                    onChange={setSelEmps}
                  />
                </div>
              </div>
              <div className="billed-matrix-filter-actions">
                <button type="button" className="btn btn-primary" onClick={run} disabled={loading}>
                  {loading ? 'Loading…' : 'Run report'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setSelCustomers([]); setSelGroups([]); setSelEmps([]); }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          </details>
        </div>

        {err ? (
          <div className="billed-matrix-error" role="alert">
            {err}
          </div>
        ) : null}

        <div className="card billed-matrix-results-card">
          <div className="card-header billed-matrix-results-head">
            <span className="billed-matrix-results-meta">
              Results
              <span className="billed-matrix-results-meta-muted">
                {' · '}
                {loading ? '…' : `${data.rows.length} row${data.rows.length === 1 ? '' : 's'}`}
                {' · '}
                ₹{fmt(grandTotal)}
              </span>
            </span>
          </div>
          <div className="billed-matrix-legend" aria-label="Cell color legend">
            <span className="billed-matrix-legend-item">
              <span className="billed-matrix-legend-swatch billed-matrix-legend-swatch--ok" aria-hidden />
              In period (billed)
            </span>
            <span className="billed-matrix-legend-item">
              <span className="billed-matrix-legend-swatch billed-matrix-legend-swatch--gap" aria-hidden />
              In period (no invoice)
            </span>
            <span className="billed-matrix-legend-item">
              <span className="billed-matrix-legend-swatch billed-matrix-legend-swatch--warn" aria-hidden />
              Outside period (with value)
            </span>
          </div>
          <div className="billed-matrix-table-frame">
            <div className="table-container billed-matrix-table-wrap">
              <table className="billed-matrix-table">
                <caption className="billed-matrix-table-caption">
                  Billed amounts by month for each customer / SOW / employee row
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="billed-matrix-th billed-matrix-th-sticky-left">Customer</th>
                    {data.has_sub_brand ? <th scope="col" className="billed-matrix-th">Sub-brand</th> : null}
                    <th scope="col" className="billed-matrix-th">RFP / project</th>
                    <th scope="col" className="billed-matrix-th billed-matrix-th-narrow">Emp ID</th>
                    <th scope="col" className="billed-matrix-th">Employee</th>
                    <th scope="col" className="billed-matrix-th billed-matrix-th-date">SOW start</th>
                    <th scope="col" className="billed-matrix-th billed-matrix-th-date">SOW end</th>
                    {data.months.map(m => (
                      <th key={m} scope="col" className="billed-matrix-th billed-matrix-th-month text-right tabular-nums">
                        {m}
                      </th>
                    ))}
                    <th scope="col" className="billed-matrix-th billed-matrix-th-sticky-right text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => {
                    const rowTotal = Object.values(r.amounts || {}).reduce((a, b) => a + Number(b || 0), 0);
                    return (
                      <tr key={`${offset + i}-${r.emp_id}-${r.rfp_num}-${r.customer_name}`} className="billed-matrix-body-row">
                        <td
                          className="billed-matrix-td billed-matrix-td-customer billed-matrix-sticky-left"
                          title={r.customer_name || ''}
                        >
                          {r.customer_name}
                        </td>
                        {data.has_sub_brand ? <td className="billed-matrix-td billed-matrix-td-muted">{r.sub_brand || '—'}</td> : null}
                        <td className="billed-matrix-td billed-matrix-td-rfp">{r.rfp_num || '—'}</td>
                        <td className="billed-matrix-td tabular-nums billed-matrix-td-narrow">{r.emp_id || '—'}</td>
                        <td className="billed-matrix-td">{r.emp_name || '—'}</td>
                        <td className="billed-matrix-td tabular-nums billed-matrix-td-date">{r.sow_start || '—'}</td>
                        <td className="billed-matrix-td tabular-nums billed-matrix-td-date">{r.sow_end || '—'}</td>
                        {data.months.map(m => {
                          const v = Number(r.amounts?.[m] || 0);
                          const tone = matrixCellTone(from, to, r, m, v);
                          return (
                            <td
                              key={m}
                              className={`billed-matrix-td billed-matrix-td-month text-right billed-matrix-cell${tone ? ` billed-matrix-cell--${tone}` : ''}`}
                            >
                              {v ? fmt(v) : ''}
                            </td>
                          );
                        })}
                        <td className="billed-matrix-td text-right tabular-nums billed-matrix-row-total billed-matrix-sticky-right">
                          {fmt(rowTotal)}
                        </td>
                      </tr>
                    );
                  })}
                  {data.rows.length === 0 ? (
                    <tr className="billed-matrix-empty-row">
                      <td colSpan={emptyColSpan}>{loading ? 'Loading…' : 'No data for the chosen filters.'}</td>
                    </tr>
                  ) : null}
                </tbody>
                {data.rows.length > 0 ? (
                  <tfoot>
                    <tr className="billed-matrix-tfoot-row">
                      <td colSpan={fixedCols} className="billed-matrix-td billed-matrix-tfoot-label billed-matrix-sticky-left">
                        Total
                      </td>
                      {data.months.map(m => (
                        <td key={m} className="billed-matrix-td billed-matrix-tfoot-num text-right">{fmt(totalsByMonth[m] || 0)}</td>
                      ))}
                      <td className="billed-matrix-td text-right billed-matrix-sticky-right billed-matrix-tfoot-grand">{fmt(grandTotal)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
            {!loading && totalRows > 0 ? (
              <div className="billed-matrix-pager" role="navigation" aria-label="Results table pagination">
                <div className="billed-matrix-pager-summary">
                  <span className="billed-matrix-pager-summary-label">Showing</span>
                  <span className="billed-matrix-pager-summary-range">
                    <strong className="tabular-nums">{rangeStart}</strong>
                    <span className="billed-matrix-pager-summary-dash">–</span>
                    <strong className="tabular-nums">{rangeEnd}</strong>
                    <span className="billed-matrix-pager-summary-of">of</span>
                    <strong className="tabular-nums">{totalRows.toLocaleString()}</strong>
                    <span className="billed-matrix-pager-summary-unit">rows</span>
                  </span>
                </div>
                <div className="billed-matrix-pager-controls">
                  <div className="billed-matrix-pager-page-size" ref={pageSizeDdRef}>
                    <span className="billed-matrix-pager-field-label" id="billed-matrix-page-size-label">
                      Rows per page
                    </span>
                    <div className="billed-matrix-pager-dd">
                      <button
                        type="button"
                        className={`billed-matrix-pager-dd-trigger ${pageSizeOpen ? 'billed-matrix-pager-dd-trigger-open' : ''}`}
                        aria-haspopup="listbox"
                        aria-expanded={pageSizeOpen}
                        aria-labelledby="billed-matrix-page-size-label"
                        aria-label={`Rows per page, current value ${pageSize}`}
                        onClick={() => setPageSizeOpen(o => !o)}
                      >
                        <span className="tabular-nums">{pageSize}</span>
                        <span className="billed-matrix-pager-dd-chevron" aria-hidden>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </span>
                      </button>
                      {pageSizeOpen ? (
                        <ul className="billed-matrix-pager-dd-menu" role="listbox" aria-labelledby="billed-matrix-page-size-label">
                          {PAGE_SIZE_OPTIONS.map(n => (
                            <li
                              key={n}
                              role="option"
                              aria-selected={pageSize === n}
                              className={
                                pageSize === n
                                  ? 'billed-matrix-pager-dd-option billed-matrix-pager-dd-option-active'
                                  : 'billed-matrix-pager-dd-option'
                              }
                              onMouseDown={e => {
                                e.preventDefault();
                                setPageSize(n);
                                setPageSizeOpen(false);
                              }}
                            >
                              {n}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                  <div className="billed-matrix-pager-nav" role="group" aria-label="Page navigation">
                    <button
                      type="button"
                      className="billed-matrix-pager-step"
                      disabled={safePage <= 1}
                      onClick={() => setPage(1)}
                      aria-label="First page"
                      title="First page"
                    >
                      <span className="billed-matrix-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 19l-7-7 7-7" />
                          <path d="M18 19l-7-7 7-7" />
                        </svg>
                      </span>
                      <span>First</span>
                    </button>
                    <button
                      type="button"
                      className="billed-matrix-pager-step"
                      disabled={safePage <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      aria-label="Previous page"
                      title="Previous page"
                    >
                      <span className="billed-matrix-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M15 18l-6-6 6-6" />
                        </svg>
                      </span>
                      <span>Prev</span>
                    </button>
                    <div className="billed-matrix-pager-chip">
                      <span className="billed-matrix-pager-chip-label">Page</span>
                      <span className="billed-matrix-pager-chip-current tabular-nums">{safePage}</span>
                      <span className="billed-matrix-pager-chip-sep">/</span>
                      <span className="billed-matrix-pager-chip-total tabular-nums">{totalPages}</span>
                    </div>
                    <button
                      type="button"
                      className="billed-matrix-pager-step"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      aria-label="Next page"
                      title="Next page"
                    >
                      <span>Next</span>
                      <span className="billed-matrix-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="billed-matrix-pager-step"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(totalPages)}
                      aria-label="Last page"
                      title="Last page"
                    >
                      <span>Last</span>
                      <span className="billed-matrix-pager-step-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 5l7 7-7 7" />
                          <path d="M6 5l7 7-7 7" />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
